/** Small formatting helpers shared by the pages. */

import { getLocale, t } from '@/i18n';

export function formatBytes(bytes: number): string {
  // French writes octets (Ko, Mo, Go), as Apple's French does; every other language here uses the Latin B
  const french = getLocale().code === 'fr';
  if (bytes < 1024) return `${String(bytes)} ${french ? 'o' : 'B'}`;
  const units = french ? ['Ko', 'Mo', 'Go'] : ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const shown = value < 10 ? new Intl.NumberFormat(getLocale().intl, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value) : String(Math.round(value));
  return `${shown} ${units[unit] ?? 'KB'}`;
}

/** `23:59:57` style countdown; `00:00:00` once the moment has passed. */
export function formatCountdown(untilIso: string, now: number): string {
  const diff = Math.max(0, Math.floor((new Date(untilIso).getTime() - now) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  const two = (n: number): string => String(n).padStart(2, '0');
  if (h >= 48) return t('format.countdownDays', { days: Math.floor(h / 24), time: `${two(h % 24)}:${two(m)}:${two(s)}` });
  return `${two(h)}:${two(m)}:${two(s)}`;
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(getLocale().intl, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** How long ago, in a few words: `just now`, `5 min ago`, `2 h ago`, `3 days ago`. */
export function formatAgo(iso: string, now: number): string {
  const minutes = Math.floor(Math.max(0, now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return t('format.justNow');
  if (minutes < 60) return t('format.minAgo', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('format.hoursAgo', { n: hours });
  const days = Math.floor(hours / 24);
  return t('format.daysAgo', { n: days });
}

export function formatConnectCode(code: string): string {
  const clean = code.toUpperCase().replace(/[^A-Z]/g, '');
  return clean.length > 3 ? `${clean.slice(0, 3)}-${clean.slice(3, 6)}` : clean;
}

export const TTL_OPTIONS = [
  { value: 600, get label() { return t('format.ttl10min'); } },
  { value: 3600, get label() { return t('format.ttl1hour'); } },
  { value: 86_400, get label() { return t('format.ttl1day'); } },
  { value: 259_200, get label() { return t('format.ttl3days'); } },
] as const;

/** How many times a bridge may be opened: the choices offered, up to the server's limit of 10. */
export const VIEW_OPTIONS = [1, 2, 3, 5, 10] as const;

export const MAX_TEXT_BYTES = 50 * 1024;
export const MAX_FILE_BYTES = 100 * 1024 * 1024;
